import { useLayoutEffect, type RefObject } from "react";
import {
  fitWidestWord,
  plateFallbackFor,
  PLATE_DESKTOP_CAP,
  PLATE_MD_BREAKPOINT,
  PLATE_MOBILE_CAP,
} from "@/lib/plate-and-hairline";

/** The largest size `.sc-marquee.is-active .sc-title` ever asks for. */
const ACTIVE_TITLE_CAP = 74;

/**
 * The widest word of `el` as the browser renders it at 100px, in whatever
 * treatment `el` currently has (weight, case, tracking all inherited).
 */
function widestWordAt100(el: HTMLElement, text: string): number {
  const style = getComputedStyle(el);
  const tracking = parseFloat(style.letterSpacing) / parseFloat(style.fontSize);
  const probe = document.createElement("span");
  probe.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font-size:100px;letter-spacing:${
    Number.isFinite(tracking) ? tracking : 0
  }em`;
  el.appendChild(probe);
  let widest = 0;
  for (const word of text.split(/\s+/)) {
    probe.textContent = word;
    widest = Math.max(widest, probe.getBoundingClientRect().width);
  }
  probe.remove();
  return widest;
}

/**
 * Keeps the expanded row's title from splitting a word.
 *
 * The expansion is sized in CSS, so nothing checked that its longest word fit
 * the row's width. This measures the words as rendered and publishes the
 * largest size at which the widest fits as `--sc-fit-size`; the stylesheet
 * takes the smaller of that and its own size. Under the plate floor it sets
 * `data-fit="fallback"`, the plate-fallback treatment.
 */
export function useFitTitle(ref: RefObject<HTMLElement | null>, text: string, active: boolean) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    let live = true;

    const fit = () => {
      const usable = el.clientWidth;
      if (!live || usable <= 0 || !text.trim()) return;
      const desktop = window.matchMedia(`(min-width: ${PLATE_MD_BREAKPOINT}px)`).matches;
      const fallbackCap = plateFallbackFor(desktop ? PLATE_DESKTOP_CAP : PLATE_MOBILE_CAP);
      const { size, isPlate } = fitWidestWord(
        (fallback) => {
          if (fallback) el.setAttribute("data-fit", "fallback");
          else el.removeAttribute("data-fit");
          return widestWordAt100(el, text);
        },
        usable,
        ACTIVE_TITLE_CAP,
        fallbackCap,
      );
      el.style.setProperty("--sc-fit-size", `${size}px`);
      if (isPlate) el.removeAttribute("data-fit");
      else el.setAttribute("data-fit", "fallback");
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    void document.fonts?.ready.then(fit);

    return () => {
      live = false;
      ro.disconnect();
      el.style.removeProperty("--sc-fit-size");
      el.removeAttribute("data-fit");
    };
  }, [ref, text, active]);
}
